import { autoinject, computedFrom } from "aurelia-framework";
import { bindable } from "aurelia-templating";
import { transient } from "aurelia-dependency-injection";
import type { CompListMenuItem } from "./comp-list-menu.models";
import { Reflection } from "../../helpers/reflection";

//Load styles
require("./comp-list-menu.scss");

@autoinject
@transient()
export class CompListMenu {

    //Is list-menu open
    public isOpen: boolean = false;

    //Depicts whether to display list-menu under or above the menu icon
    public menuContentPosition: 'top' | 'bottom';

    @bindable
    public label: string;

    @bindable
    public menuItems: CompListMenuItem[];

    @bindable
    public theme: string = "primary";

    @bindable
    public size: "small" | "medium" = "medium";

    @bindable
    public disabled: boolean = false;

    //Hide list-menu if it has no items
    @bindable
    public hideIfNoItems: boolean = true;

    @bindable
    public hideMenuBorder: boolean = false;

    //Global/singleton list-menu. Represents the currently/latest active list-menu
    private static _openedListMenu: CompListMenu;

    //List-menu element
    private listMenu: HTMLElement;

    //The element being scrolled
    private scrollView: ChildNode = undefined;

    constructor() {
    }

    attached(): void {
        //Initially re-position filter content based on window height
       this.rePositionContent();
       window.addEventListener('resize', this.rePositionContent);

       setTimeout(() => {
           this.scrollView = document.getElementsByTagName('router-view')[0].children[0];
           this.scrollView.addEventListener('scroll', this.rePositionContent);
       }, 1000);
    }

    detached(): void {
        window.removeEventListener('resize', this.rePositionContent);
        this.scrollView?.removeEventListener('scroll', this.rePositionContent);
    }

    //Reposition the content that is displayed when the menu is opened
    rePositionContent = () => {
        if (!this.listMenu) return;

        //Get space between menu and the window's bottom edge
        const filterElementPosY = window.innerHeight - this.listMenu.getBoundingClientRect().bottom;

        /* If the menu items overlap with the window's bottom side, and there is ample space for the menu items on top of the menu,
        /* display the menu items on top of the menu, else beneath the menu.  (The '100' value is a buffer for between the menu items
        and the window's edges */

        //If there is ample space below the menu for the menu items
        this.menuContentPosition = (filterElementPosY > ((this.menuItems?.length * 40) + 100) ||
            //OR if there is not enough space for the menu items on the top side of the menu
            ((window.innerHeight - filterElementPosY) < ((this.menuItems?.length * 40) + 100))) ? 'bottom' : 'top';
    }

    //Toggle list menu (display/hide menu items)
    toggle = ($event) => {
        this.stopEvent($event);
        if (this.disabled) return;

        //If the current list-menu was open
        if (this.isOpen) {
            this.close();
        }
        //Check if another list-menu is open
        else {
            //If another list-menu was open (else the current list menu would've been opened)
            if (CompListMenu._openedListMenu?.isOpen) {
                CompListMenu._openedListMenu.close();
            }
            this.open();
        }
    }

    /**
    * When an item within the list-menu is clicked
    * @param $event Browser event
    * @param item List-menu item
    * @param index item index
    */
    internalClick($event, item, index: number): void {
        this.stopEvent($event);
        if (item?.click && typeof item.click === "function") {
            item.click(index);
            this.close();
        }
    }

    //Close current list menu
    close = () => {
        this.isOpen = false;
        if (CompListMenu._openedListMenu == this) {
            CompListMenu._openedListMenu = null;
        }
        document.removeEventListener("click", this.close, false);
    }

    //Open current list menu
    open = () => {
        CompListMenu._openedListMenu = this;
        this.isOpen = true;
        document.addEventListener("click", this.close, false);
    }

    //Stop event propogation
    stopEvent(e): void {
        if (e.stopPropagation) {
            e.stopPropagation();
        }
        if (e.preventDefault) {
            e.preventDefault();
        }
        e.cancelBubble = true;
        e.returnValue = false;
    }

    //Check if there are visible menu items
    @computedFrom(Reflection.getPropertyPath<CompListMenu>(z => z.menuItems), Reflection.getPropertyPath<CompListMenu>(z => z.menuItems.length))
    get hasVisibleItems()
    {
        if(!this.menuItems) return false;

        const visibleMenuItems = this.menuItems.filter((menuItem: CompListMenuItem) =>
        {
            return typeof(menuItem.isVisible) == "function" ? menuItem.isVisible(): menuItem.isVisible
        });

        return visibleMenuItems?.length > 0;
    }
}
